/**
 * SessionGate — wraps the route tree with two session-safety nets:
 *  1. Listens for authEvents' session-expired signal (fired by AuthSessionExpiredError
 *     anywhere in the app) and forces a clean sign-out instead of leaving the UI
 *     silently broken while still looking "up".
 *  2. A 30-minute idle timer with a 60s countdown warning before auto sign-out.
 * Both paths clear the local MSAL cache (no popup/redirect — avoids the browser
 * blocking a popup that wasn't triggered by a direct user click) and route to
 * /session-expired, where the user can explicitly sign in again.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogContent,
    DialogActions,
    Button,
} from '@fluentui/react-components';
import { onSessionExpired } from '../services/authEvents';
import { useIdleTimer } from '../hooks/useIdleTimer';
import { IDLE_TIMEOUT_MS, IDLE_WARNING_MS } from '../config/constants';

interface Props {
    children: React.ReactNode;
}

export function SessionGate({ children }: Props) {
    const isAuthenticated = useIsAuthenticated();
    const { instance } = useMsal();
    const navigate = useNavigate();

    // Guards against both paths firing in the same session (event + idle timeout
    // racing) and forcing a double navigation.
    const signedOutRef = useRef(false);

    const forceSignOut = useCallback(async (reason: 'expired' | 'idle') => {
        if (signedOutRef.current) return;
        signedOutRef.current = true;
        try {
            await instance.clearCache();
        } catch {
            // Best-effort — we're navigating to a sign-in prompt either way.
        }
        navigate(`/session-expired?reason=${reason}`, { replace: true });
    }, [instance, navigate]);

    useEffect(() => {
        return onSessionExpired(() => { void forceSignOut('expired'); });
    }, [forceSignOut]);

    const { isWarning, secondsRemaining, stayActive } = useIdleTimer({
        enabled: isAuthenticated,
        idleTimeoutMs: IDLE_TIMEOUT_MS,
        warningMs: IDLE_WARNING_MS,
        onTimeout: () => { void forceSignOut('idle'); },
    });

    return (
        <>
            {children}
            <Dialog open={isWarning}>
                <DialogSurface>
                    <DialogTitle>You've been inactive</DialogTitle>
                    <DialogBody>
                        <DialogContent>
                            You'll be signed out in <strong>{secondsRemaining}s</strong> due to inactivity.
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="primary" onClick={stayActive}>
                                Stay signed in
                            </Button>
                            <Button appearance="subtle" onClick={() => { void forceSignOut('idle'); }}>
                                Sign out now
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
}
